import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router";
import { quotesApi, type QuoteDetailDto } from "@/lib/quotes";
import { QuoteStatusBadge } from "@/components/quotes/quote-status-badge";
import { QuoteItemsGrid, type QuoteItemRow } from "@/components/quotes/quote-items-grid";
import { QuoteSendButton } from "@/components/quotes/quote-send-button";
import { QuotePdfLink } from "@/components/quotes/quote-pdf-link";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { PageContainer } from "@/app/layouts/PageContainer";
import { toast } from "@/hooks/use-toast";
import { useState } from "react";

// ---------------------------------------------------------------------------
// Currency formatter
// ---------------------------------------------------------------------------

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });

// ---------------------------------------------------------------------------
// OverviewRow
// ---------------------------------------------------------------------------

function OverviewRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value ?? "—"}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// OverviewTab
// ---------------------------------------------------------------------------

function OverviewTab({ data }: { data: QuoteDetailDto }) {
  return (
    <dl className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
      <OverviewRow label="Client" value={data.clientName} />
      <OverviewRow label="Owner" value={data.ownerEmployeeName} />
      <OverviewRow
        label="Branch"
        value={data.branchId != null ? `Branch #${data.branchId}` : null}
      />
      <OverviewRow label="Description" value={data.description} />
      <OverviewRow label="Approval type" value={data.approvalTypeCode} />
      <OverviewRow
        label="Valid until"
        value={data.validUntil ? new Date(data.validUntil).toLocaleDateString("en-AU") : null}
      />
      <OverviewRow
        label="Total ex-GST"
        value={data.totalExGst != null ? fmt.format(data.totalExGst) : null}
      />
      <OverviewRow
        label="GST"
        value={data.gst != null ? fmt.format(data.gst) : null}
      />
      <OverviewRow
        label="Total inc-GST"
        value={data.totalIncGst != null ? fmt.format(data.totalIncGst) : null}
      />
      {data.enquiryId != null && (
        <OverviewRow
          label="Linked enquiry"
          value={<a href={`/enquiries/${data.enquiryId}`} className="text-primary underline">Enquiry #{data.enquiryId}</a>}
        />
      )}
      {data.convertedToJobId != null && (
        <OverviewRow
          label="Converted to job"
          value={<a href={`/jobs/${data.convertedToJobId}`} className="text-primary underline">Job #{data.convertedToJobId}</a>}
        />
      )}
    </dl>
  );
}

// ---------------------------------------------------------------------------
// ItemsTab
// ---------------------------------------------------------------------------

function ItemsTab({ quoteId }: { quoteId: number }) {
  const qc = useQueryClient();
  const [localItems, setLocalItems] = useState<QuoteItemRow[] | null>(null);
  const [saving, setSaving] = useState(false);

  const { data: serverItems = [], isLoading } = useQuery({
    queryKey: ["quote-items", quoteId],
    queryFn: () => quotesApi.items(quoteId),
    select: (items) =>
      items.map((i) => ({
        quoteItemId: i.quoteItemId,
        sortOrder: i.sortOrder,
        description: i.description,
        quantity: i.quantity,
        unitPriceExGst: i.unitPriceExGst,
        lineTotalExGst: i.lineTotalExGst,
        notes: i.notes,
      })) as QuoteItemRow[],
  });

  const items = localItems ?? serverItems;

  async function handleSave() {
    setSaving(true);
    try {
      const existingIds = new Set(serverItems.map((i) => i.quoteItemId));
      // Delete removed items
      for (const si of serverItems) {
        if (!items.find((i) => i.quoteItemId === si.quoteItemId)) {
          await quotesApi.deleteItem(quoteId, si.quoteItemId);
        }
      }
      // Add new / update existing
      for (const item of items) {
        if (!existingIds.has(item.quoteItemId)) {
          await quotesApi.addItem(quoteId, {
            sortOrder: item.sortOrder,
            description: item.description,
            quantity: item.quantity,
            unitPriceExGst: item.unitPriceExGst,
            notes: item.notes ?? null,
          });
        }
      }
      toast.success("Items saved.");
      void qc.invalidateQueries({ queryKey: ["quote-items", quoteId] });
      void qc.invalidateQueries({ queryKey: ["quote", quoteId] });
      setLocalItems(null);
    } catch (err) {
      toast.error(`Save failed: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setSaving(false);
    }
  }

  if (isLoading) return <p className="text-sm text-muted-foreground mt-4">Loading…</p>;

  return (
    <div className="mt-4 space-y-3">
      <QuoteItemsGrid items={items} onChange={setLocalItems} />
      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            setLocalItems([
              ...items,
              {
                quoteItemId: -Date.now(),
                sortOrder: (items.length + 1) * 10,
                description: "",
                quantity: 1,
                unitPriceExGst: 0,
                lineTotalExGst: 0,
              },
            ])
          }
        >
          + Add row
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save items"}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// OptionsTab — minimal live stub
// ---------------------------------------------------------------------------

function OptionsTab({ quoteId }: { quoteId: number }) {
  const { data: options = [], isLoading } = useQuery({
    queryKey: ["quote-options", quoteId],
    queryFn: () => quotesApi.options(quoteId),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground mt-4">Loading…</p>;

  return (
    <div className="mt-4">
      {options.length === 0 ? (
        <p className="text-sm text-muted-foreground">No options defined.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2">Description</th>
              <th className="text-right py-2">Price delta</th>
              <th className="text-left py-2">Default</th>
            </tr>
          </thead>
          <tbody>
            {options.map((o) => (
              <tr key={o.quoteOptionId} className="border-t">
                <td className="py-1">{o.description}</td>
                <td className="py-1 text-right">
                  {o.priceDelta != null ? fmt.format(o.priceDelta) : "—"}
                </td>
                <td className="py-1">{o.isSelectedDefault ? "Yes" : "No"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// IncludesTab — minimal live stub
// ---------------------------------------------------------------------------

function IncludesTab({ quoteId }: { quoteId: number }) {
  const { data: includes = [], isLoading } = useQuery({
    queryKey: ["quote-includes", quoteId],
    queryFn: () => quotesApi.includes(quoteId),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground mt-4">Loading…</p>;

  return (
    <div className="mt-4">
      {includes.length === 0 ? (
        <p className="text-sm text-muted-foreground">No includes defined.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2">Description</th>
              <th className="text-left py-2">Included</th>
            </tr>
          </thead>
          <tbody>
            {includes.map((inc) => (
              <tr key={inc.quoteIncludeId} className="border-t">
                <td className="py-1">{inc.description}</td>
                <td className="py-1">{inc.included ? "Yes" : "No"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TandCsTab — minimal live stub
// ---------------------------------------------------------------------------

function TandCsTab({ quoteId }: { quoteId: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ["quote-tandcs", quoteId],
    queryFn: () => quotesApi.getTandCs(quoteId),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground mt-4">Loading…</p>;

  return (
    <div className="mt-4">
      {data?.tandCsText ? (
        <pre className="whitespace-pre-wrap text-sm border rounded p-4 bg-muted">{data.tandCsText}</pre>
      ) : (
        <p className="text-sm text-muted-foreground">No T&Cs set for this quote.</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SkeletonDetail
// ---------------------------------------------------------------------------

function SkeletonDetail() {
  return (
    <PageContainer>
      <div className="animate-pulse space-y-4">
        <div className="h-8 w-64 rounded bg-muted" />
        <div className="h-4 w-48 rounded bg-muted" />
        <div className="h-64 rounded bg-muted" />
      </div>
    </PageContainer>
  );
}

// ---------------------------------------------------------------------------
// QuoteDetailPage
// ---------------------------------------------------------------------------

export function QuoteDetailPage() {
  const { id = "0" } = useParams();
  const qid = Number(id);
  const qc = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["quote", qid],
    queryFn: () => quotesApi.get(qid),
    enabled: !!qid,
  });

  const duplicate = useMutation({
    mutationFn: () => quotesApi.duplicate(qid),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ["quotes"] });
      toast.success(`Duplicate quote ${res.quoteRef} created.`);
    },
    onError: (err: Error) => toast.error(`Duplicate failed: ${err.message}`),
  });

  if (isLoading) return <SkeletonDetail />;
  if (isError || !data) {
    return (
      <PageContainer>
        <p className="p-6 text-sm text-muted-foreground">Quote not found.</p>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Header */}
      <header className="mb-6 flex flex-wrap items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h1 className="text-2xl font-semibold">{data.quoteRef}</h1>
            <span className="text-muted-foreground">·</span>
            <span className="text-base text-muted-foreground">v{data.quoteVersionNo}</span>
            <span className="text-muted-foreground">·</span>
            <QuoteStatusBadge status={data.status} />
          </div>
          {data.description && (
            <p className="text-sm text-muted-foreground truncate">{data.description}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 shrink-0">
          <QuoteSendButton
            quoteId={data.quoteId}
            status={data.status}
            convertedToJobId={data.convertedToJobId}
            defaultCustomerEmail=""
          />
          {data.status !== "Draft" && <QuotePdfLink quoteId={data.quoteId} />}
          <Button
            variant="outline"
            onClick={() => duplicate.mutate()}
            disabled={duplicate.isPending}
          >
            Duplicate
          </Button>
        </div>
      </header>

      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="items">Items</TabsTrigger>
          <TabsTrigger value="options">Options</TabsTrigger>
          <TabsTrigger value="includes">Includes</TabsTrigger>
          <TabsTrigger value="tandcs">T&Cs</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
          <TabsTrigger value="bom" disabled>
            BOM (BOM domain)
          </TabsTrigger>
          <TabsTrigger value="files" disabled>
            Files (Plan III)
          </TabsTrigger>
          <TabsTrigger value="emails" disabled>
            Emails (Plan III)
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab data={data} />
        </TabsContent>

        <TabsContent value="items">
          <ItemsTab quoteId={qid} />
        </TabsContent>

        <TabsContent value="options">
          <OptionsTab quoteId={qid} />
        </TabsContent>

        <TabsContent value="includes">
          <IncludesTab quoteId={qid} />
        </TabsContent>

        <TabsContent value="tandcs">
          <TandCsTab quoteId={qid} />
        </TabsContent>

        <TabsContent value="history">
          <div className="mt-4">
            <p className="text-sm text-muted-foreground">
              Audit history is available in a future plan.
            </p>
          </div>
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}
